/**
 * Central mutable app state. Kept as a plain object (no framework) because
 * everything downstream is imperative canvas work. Modules import `state`
 * directly; `main.ts` owns the DOM and is the only place that reads UI
 * controls into state.
 */

export type Side = 'A' | 'B';

export interface Transform {
  /** 1.0 = "cover" fit. Range MIN_SCALE..MAX_SCALE. */
  scale: number;
  /** Pan offsets in *source image* pixels, applied to the crop center. */
  offsetX: number;
  offsetY: number;
}

/**
 * Photos load at 112% on purpose: at exactly 100% (cover fit) one axis has
 * zero pan room by geometry, so starting slightly zoomed in guarantees pan
 * room on both axes immediately.
 */
export const DEFAULT_SCALE = 1.12;
export const MIN_SCALE = 1;
export const MAX_SCALE = 3;

export const defaultTransform = (): Transform => ({
  scale: DEFAULT_SCALE,
  offsetX: 0,
  offsetY: 0,
});

export interface LoadedImage {
  /** Downscaled (<=1600px) offscreen canvas holding the decoded pixels. */
  bitmap: HTMLCanvasElement;
  /** JPEG data URL for the upload-slot thumbnail. */
  thumb: string;
  name: string;
}

/**
 * Reels, TikTok, Stories, and YouTube Shorts all share the 9:16 ratio, so
 * "Shorts" is covered by that one preset's label rather than a duplicate
 * entry at the same size. 16:9 and 8.5:11 are the two genuinely different
 * shapes the spec calls out: a landscape frame for embedding on a website,
 * and a print-page frame for an in-office consult handout.
 */
export type AspectKey = '9:16' | '4:5' | '1:1' | '16:9' | '8.5:11';
export const ASPECTS: Record<AspectKey, readonly [number, number]> = {
  '9:16': [720, 1280],
  '4:5': [864, 1080],
  '1:1': [900, 900],
  '16:9': [1280, 720],
  // 200px/inch on an 8.5x11 page — sharp enough to print, not excessive.
  '8.5:11': [1700, 2200],
};

export type Direction = 'ltr' | 'rtl';
export type ExportMode = 'auto' | 'manual' | 'custom' | 'still';

/**
 * One before/after comparison: its own photos, alignment, and locks. A
 * "multi-angle" export sweeps through several of these (e.g. frontal +
 * oblique) in one video; everything else in the app operates on whichever
 * set is currently active.
 */
export interface PhotoSet {
  id: string;
  /** Display label, e.g. "Angle 1". Renumbered on removal to stay contiguous. */
  label: string;
  images: Record<Side, LoadedImage | null>;
  transforms: Record<Side, Transform>;
  locked: Record<Side, boolean>;
}

/** Real before/after sets are rarely more than frontal + oblique + profile. */
export const MAX_SETS = 3;

let setIdSeq = 0;
export function newPhotoSet(label: string): PhotoSet {
  setIdSeq += 1;
  return {
    id: `set-${setIdSeq}`,
    label,
    images: { A: null, B: null },
    transforms: { A: defaultTransform(), B: defaultTransform() },
    locked: { A: false, B: false },
  };
}

export interface AppState {
  sets: PhotoSet[];
  /** Which set the alignment UI and live canvas currently show. */
  activeSetIndex: number;
  /** Divider position in canvas pixels. */
  sliderX: number;
  aspect: AspectKey;
  direction: Direction;
  showLabels: boolean;
  showGuide: boolean;
  /** A video export is in progress (any mode). */
  recording: boolean;
  /** The divider is being driven by a timeline (auto sweep, pattern preview/export). */
  autoAnimating: boolean;
  /** Custom-pattern capture is in progress (not a video recording). */
  pathRecording: boolean;
}

export const state: AppState = {
  sets: [newPhotoSet('Angle 1')],
  activeSetIndex: 0,
  sliderX: 0,
  aspect: '9:16',
  direction: 'ltr',
  showLabels: true,
  showGuide: false,
  recording: false,
  autoAnimating: false,
  pathRecording: false,
};

export function activeSet(): PhotoSet {
  return state.sets[state.activeSetIndex]!;
}

export function setIsComplete(set: PhotoSet): boolean {
  return set.images.A !== null && set.images.B !== null;
}

/** Does the *active* set have both photos? (Most of the UI cares only about this.) */
export function hasBothImages(): boolean {
  return setIsComplete(activeSet());
}

export function anySetComplete(): boolean {
  return state.sets.some(setIsComplete);
}

/** Sets with both photos, in tab order — what a multi-angle export sweeps through. */
export function completeSets(): PhotoSet[] {
  return state.sets.filter(setIsComplete);
}

export function otherSide(side: Side): Side {
  return side === 'A' ? 'B' : 'A';
}
